import { getBranding, type PortalKey } from "@/branding";
import type { Component } from "vue";
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import EcolabelCompanyView from "./ecolabel/EcolabelCompanyView.vue";
import EcolabelSidenav from "./ecolabel/EcolabelSidenav.vue";
import PoweredBySolderaLockup from "./ecolabel/PoweredBySolderaLockup.vue";
import SolderaSidenav from "./soldera/SolderaSidenav.vue";

/**
 * Portal registry: everything that differs between the full Soldera app and a
 * restricted white-label portal lives here, so shared components stay free of
 * brand conditionals. A new ecolabel-provider portal is a new Brand in
 * `@/branding` pointing at the existing "ecolabel" portal; a portal with a
 * different shape is a new entry here.
 */
export interface PortalConfig {
  key: PortalKey;
  /** Sidebar contents rendered by UserFacingLayout. Receives `accountData`. */
  Sidenav: Component;
  /** Optional element rendered next to the brand logo in the top nav. */
  TopNavAccessory: Component | null;
  /**
   * When set, DashboardView renders this instead of the producer/corporate
   * dashboard variants. Receives `company`.
   */
  CompanyDashboard: Component | null;
  /** Whether the consolidated multi-company dashboard exists on this portal. */
  hasConsolidatedView: boolean;
  /** Route names that redirect to `fallbackRouteName` on this portal. */
  blockedRouteNames: ReadonlySet<string>;
  /** Matched route paths (e.g. "/admin") that redirect likewise. */
  blockedPathPrefixes: readonly string[];
  /** Where blocked navigation lands. Unused when nothing is blocked. */
  fallbackRouteName: string;
}

export const getPortal = (): PortalConfig => PORTALS[getBranding().portal];

/**
 * Router guard for restricted portals: navigation to a blocked route name, a
 * blocked path prefix, or the consolidated dashboard (when the portal has
 * none) redirects to the portal's fallback route. Returns `true` to let the
 * navigation proceed. Presentation only — server-side authorization is
 * unchanged.
 */
export const portalNavigationRedirect = (
  to: RouteLocationNormalized
): RouteLocationRaw | true => {
  const portal = getPortal();
  const fallback = { name: portal.fallbackRouteName };
  if (typeof to.name === "string" && portal.blockedRouteNames.has(to.name)) {
    return fallback;
  }
  if (
    portal.blockedPathPrefixes.length > 0 &&
    to.matched.some((record) =>
      portal.blockedPathPrefixes.includes(record.path)
    )
  ) {
    return fallback;
  }
  if (
    !portal.hasConsolidatedView &&
    to.name === "Dashboard" &&
    to.query.consolidated === "true"
  ) {
    return fallback;
  }
  return true;
};

const PORTALS: Record<PortalKey, PortalConfig> = {
  soldera: {
    key: "soldera",
    Sidenav: SolderaSidenav,
    TopNavAccessory: null,
    CompanyDashboard: null,
    hasConsolidatedView: true,
    blockedRouteNames: new Set(),
    blockedPathPrefixes: [],
    fallbackRouteName: "Root",
  },
  ecolabel: {
    key: "ecolabel",
    Sidenav: EcolabelSidenav,
    TopNavAccessory: PoweredBySolderaLockup,
    CompanyDashboard: EcolabelCompanyView,
    hasConsolidatedView: false,
    // Producer/signup surfaces stay on the Soldera portal. This is
    // presentation, not access control — server-side authorization is
    // unchanged.
    blockedRouteNames: new Set([
      "Root",
      "MarketInsights",
      "RoleSelection",
      "ProducerSignUp",
      "CorporateSignUp",
    ]),
    // The forwards pages live under /admin, and /forwards only redirects there.
    blockedPathPrefixes: ["/admin", "/soldera-inventory"],
    fallbackRouteName: "Ecolabels",
  },
};
